import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useAuth } from "../context/AuthContext";

export default function AuthPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const redirect = params.get("redirect") || "";
  const [mode, setMode] = useState(params.get("mode") === "login" ? "login" : "register");
  const [role, setRole] = useState(params.get("role") === "caregiver" ? "caregiver" : "family");
  const [form, setForm] = useState({ name: "", email: "", phone: "", location: "", password: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const destFor = (u) => redirect || (u.role === "admin" ? "/admin" : u.role === "caregiver" ? "/caregiver" : "/dashboard");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let u;
      if (mode === "login") {
        u = await login(form.email, form.password);
      } else {
        if (!form.name || !form.phone || !form.location) {
          toast.error("Please fill in all fields.");
          setLoading(false);
          return;
        }
        u = await register({ role, ...form });
      }
      toast.success(mode === "login" ? "Welcome back!" : "Account created!");
      navigate(destFor(u), { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Header />
      <div className="hero-gradient min-h-[calc(100vh-100px)] py-14 px-5">
        <div className="max-w-md mx-auto bg-white rounded-2xl border border-black/10 shadow-[0_20px_50px_-20px_rgba(124,34,51,0.2)] p-7">
          <h1 className="font-display text-3xl text-[#1f1a1b]">{mode === "login" ? "Log in" : "Create your account"}</h1>
          <p className="text-[15px] text-[#5a5254] mt-1">
            {mode === "login"
              ? "Welcome back to SimplySitter."
              : role === "caregiver"
              ? "Join SimplySitter — free for caregivers, forever. No fees, ever."
              : "Join SimplySitter — 1 month free, then $15.99/month."}
          </p>

          {mode === "register" && (
            <div className="grid grid-cols-2 gap-2 mt-5">
              {[["family", "I'm a family"], ["caregiver", "I'm a caregiver"]].map(([r, label]) => (
                <button key={r} type="button" onClick={() => setRole(r)}
                  className={`rounded-xl border px-3 py-2.5 text-[14px] font-medium transition-all ${role === r ? "border-[#7c2233] bg-[#f7e4e6] text-[#7c2233]" : "border-black/10 text-[#5a5254]"}`}>
                  {label}
                </button>
              ))}
            </div>
          )}

          <form onSubmit={submit} className="mt-5 space-y-3">
            {mode === "register" && (
              <Input label="Full name" value={form.name} onChange={set("name")} placeholder="Jane Doe" />
            )}
            <Input label="Email" type="email" value={form.email} onChange={set("email")} placeholder="jane@email.com" />
            {mode === "register" && (
              <>
                <Input label="Phone" value={form.phone} onChange={set("phone")} placeholder="(555) 123-4567" />
                <Input label="City / location" value={form.location} onChange={set("location")} placeholder="e.g. New York" />
              </>
            )}
            <Input label="Password" type="password" value={form.password} onChange={set("password")} placeholder="••••••••" />
            <button type="submit" disabled={loading}
              className="w-full h-12 inline-flex items-center justify-center gap-2 rounded-xl bg-[#7c2233] hover:bg-[#661b29] disabled:opacity-70 text-white font-medium transition-colors">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {mode === "login" ? "Log in" : "Create account"}
            </button>
          </form>

          <p className="text-[14px] text-[#5a5254] mt-5 text-center">
            {mode === "login" ? "New to SimplySitter? " : "Already have an account? "}
            <button onClick={() => setMode(mode === "login" ? "register" : "login")} className="text-[#7c2233] font-medium hover:underline">
              {mode === "login" ? "Create an account" : "Log in"}
            </button>
          </p>
        </div>
      </div>
      <Footer />
    </div>
  );
}

function Input({ label, type = "text", value, onChange, placeholder }) {
  return (
    <div>
      <label className="text-[13px] font-medium text-[#3a3436]">{label}</label>
      <input type={type} value={value} onChange={onChange} placeholder={placeholder} required
        className="mt-1.5 w-full h-11 rounded-xl border border-black/10 px-3 text-[15px] outline-none focus:border-[#7c2233] focus:ring-2 focus:ring-[#7c2233]/15 transition" />
    </div>
  );
}
